import { createApp, nextTick } from "vue";
import { createPinia } from "pinia";
import dayjs from "@/plugins/datetime";

import "./index.imports.css"; 
import "./index.scss"; 

import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import * as Sentry from "@sentry/vue";

import AppWrapper from "./AppWrapper.vue";
import router from "./router";

// Vuetify
import "@mdi/font/css/materialdesignicons.css";
import "vuetify/styles";
import createVuetify from "@/plugins/vuetify";

import { VueQueryPlugin } from "@tanstack/vue-query";

import FieldInputSelect from '@/components/fields/InputSelect.vue';

// CASL
import { Can, abilitiesPlugin } from "@casl/vue";
import { ability } from "@/services/ability";

// LOCALES
// const i18n = createI18n({
//   legacy: false,
//   locale: "en",
//   fallbackLocale: "en",
//   silentTranslationWarn: true,
// });

import { i18n } from "@/locales";

const app = createApp(AppWrapper);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN || null,
  release: import.meta.env.VITE_SENTRY_RELEASE || "unknown",
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "unknown",

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", RegExp(import.meta.env.VITE_SENTRY_API_ORIGIN)],
  //routingInstrumentation: Sentry.vueRouterInstrumentation(router)

  // Performance Monitoring
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "1.0"), // Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: parseFloat(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.1"
  ), // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
app.use(router);
app.use(i18n as any);
app.use(dayjs);
app.use(createVuetify);
app.use(VueQueryPlugin);
app.use(abilitiesPlugin, ability, {
  useGlobalProperties: true,
});
app.component("Can", Can);
app.component("FieldInputSelect", FieldInputSelect);

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    $appName: string;
    $mediaBase: string;
    $defaultFormatting: {
      [key: string]: {
        [format: string]: string;
      };
    };
  }
}

app.config.globalProperties.$appName = "Vortex";
app.config.globalProperties.$mediaBase = import.meta.env.VITE_MEDIA_BASE_URL;

router.afterEach(to => {
  nextTick(() => {
    let title = (to.meta.title as string) || "";
    if (title.length > 0) {
      title += " // ";
    }
    title += app.config.globalProperties.$appName;
    document.title = title;
  });
});

app.mount("#app");
